import type { JSX } from "solid-js";

const DEFAULT_DURATION = 500;

interface AnimatedSectionProps {
  /**
   * Whether the section should be visible and expanded.
   */
  show: boolean;
  /**
   * Content to render inside the animated section.
   */
  children: JSX.Element;
  /**
   * Optional CSS class for the container.
   */
  class?: string;
  /**
   * Duration of the transition in milliseconds.
   * @default 500
   */
  duration?: number;
}

/**
 * Ingenious CSS-only height and opacity transition component.
 * Uses grid-template-rows to animate from 0 to 'auto' height without JS calculations.
 * This is highly performant on mobile as it relies on native layout transitions.
 */
export const AnimatedSection = (props: AnimatedSectionProps) => {
  const duration = () => props.duration ?? DEFAULT_DURATION;

  return (
    <div
      // Collapsed content is visually gone (0fr row + opacity 0) but stays in
      // the DOM, so it must also be hidden from assistive tech — otherwise a
      // screen reader announces a "server is not responding" banner that is no
      // longer shown. `aria-hidden` is always rendered (never removed) so it is
      // an honest, queryable signal of the section's shown/collapsed state.
      aria-hidden={props.show ? "false" : "true"}
      class={`grid transition-[grid-template-rows,opacity] ease-in-out ${props.class ?? ""}`}
      style={{
        "grid-template-rows": props.show ? "1fr" : "0fr",
        opacity: props.show ? 1 : 0,
        "transition-duration": `${duration()}ms`,
      }}
    >
      <div class="overflow-hidden">{props.children}</div>
    </div>
  );
};
